import { ModalProvider } from '@rocketmakers/armstrong-edge';
import { useStaticQuery, graphql } from 'gatsby';
import * as React from 'react';
import { AppContextQuery } from '../generated/graphql';
import { Categories, Category, ILocation, parseCategory, SeedLevel, SeedLevels } from '../typings/data';
import { orderBy } from 'lodash';

interface IAppContext {
  allLocations: ILocation[];
  filteredLocations: ILocation[];
  filterCategories: Category[];
  filterSeedLevels: SeedLevel[];
  setFilterCategories: (category: Category) => void;
  setFilterSeedLevels: (seedLevel: SeedLevel) => void;
  resetFilterCategories?: () => void;
}

export const AppContext = React.createContext<IAppContext>({
  allLocations: [],
  filteredLocations: [],
  filterCategories: [...Categories],
  setFilterCategories: () => {},
  filterSeedLevels: [...SeedLevels],
  setFilterSeedLevels: () => {},
  resetFilterCategories: () => {},
});

const AppProvider: React.FC = ({ children }) => {
  const [filterCategories, setFilterCategories] = React.useState<Category[]>([]);
  const [filterSeedLevels, setFilterSeedLevels] = React.useState<SeedLevel[]>([
    'Pre-Seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C',
    'Unknown',
  ]);

  const updateCategories = (category: Category) => {
    if (filterCategories.indexOf(category) === -1) {
      setFilterCategories((cs) => cs.concat(category));
      if (category === 'creative') {
        if (filterCategories.indexOf('media') === -1) {
          setFilterCategories((cs) => cs.concat('media'));
        }
        if (filterCategories.indexOf('gaming') === -1) {
          setFilterCategories((cs) => cs.concat('gaming'));
        }
        if (filterCategories.indexOf('immersive') === -1) {
          setFilterCategories((cs) => cs.concat('immersive'));
        }
      }
      if (category === 'deep-tech') {
        if (filterCategories.indexOf('telecoms') === -1) {
          setFilterCategories((cs) => cs.concat('telecoms'));
        }
        if (filterCategories.indexOf('ai') === -1) {
          setFilterCategories((cs) => cs.concat('ai'));
        }
        if (filterCategories.indexOf('data') === -1) {
          setFilterCategories((cs) => cs.concat('data'));
        }
        if (filterCategories.indexOf('hardware') === -1) {
          setFilterCategories((cs) => cs.concat('hardware'));
        }
        if (filterCategories.indexOf('robotics') === -1) {
          setFilterCategories((cs) => cs.concat('robotics'));
        }
      }
      if (category === 'all') {
        if (filterCategories.indexOf('all') === -1) {
          setFilterCategories((cs) => cs.concat(Categories));
          setFilterSeedLevels((cs) => cs.concat(SeedLevels));
        }
      }
    } else {
      setFilterCategories(filterCategories.filter((c) => c !== category));
      if (category === 'all') {
        setFilterCategories([]);
        setFilterSeedLevels([]);
      }
      if (category === 'creative') {
        setFilterCategories(
          filterCategories.filter((c) => c !== 'media' && c !== 'gaming' && c !== 'immersive' && c !== 'creative')
        );
      }
      if (category === 'deep-tech') {
        setFilterCategories(
          filterCategories.filter(
            (c) =>
              c !== 'robotics' &&
              c !== 'ai' &&
              c !== 'data' &&
              c !== 'telecoms' &&
              c !== 'hardware' &&
              c !== 'deep-tech'
          )
        );
      }
    }
  };

  const updateSeedLevels = (seedLevel: SeedLevel) => {
    if (filterSeedLevels.indexOf(seedLevel) === -1) {
      setFilterSeedLevels((cs) => cs.concat(seedLevel));
    } else {
      setFilterSeedLevels(filterSeedLevels.filter((c) => c !== seedLevel));
    }
  };

  const data = useStaticQuery<AppContextQuery>(graphql`
    query AppContext {
      allGoogleClustermapmainSheet {
        nodes {
          category
          company
          description
          latLng
          featured
          linkedin
          postcode
          twitter
          uRL
          investment
        }
      }
    }
  `);

  const allLocations = React.useMemo(() => {
    const techSparkNodes = data.allGoogleClustermapmainSheet.nodes
      .filter((n) => !!n.category)
      .filter((n) => !!n.latLng)
      .map((n, i) => ({
        index: i,
        name: n.company!,
        description: n.description,
        postcode: n.postcode!,
        webUrl: n.uRL,
        investment: n.investment,
        twitterUrl: n.twitter,
        featured: n.featured,
        category: parseCategory(n.category!),
        location: [parseFloat(n.latLng!.split(',')[0]), parseFloat(n.latLng!.split(',')[1])],
      }));

    return [...techSparkNodes] as any[];
  }, [data, filterCategories]);

  const filteredLocations = React.useMemo(() => {
    if (filterCategories.length === 0) {
      return orderBy(allLocations, (l) => l.category);
    }
    return orderBy(
      allLocations.filter((l) => filterCategories.indexOf(l.category) !== -1),
      (l) => l.category
    );
  }, [allLocations, filterCategories]);

  return (
    <ModalProvider>
      <AppContext.Provider
        value={{
          allLocations,
          filteredLocations,
          filterCategories,
          filterSeedLevels,
          setFilterCategories: updateCategories,
          setFilterSeedLevels: updateSeedLevels,
          resetFilterCategories: () => setFilterCategories([]),
        }}
      >
        {children}
      </AppContext.Provider>
    </ModalProvider>
  );
};

export default ({ element }) => <AppProvider>{element}</AppProvider>;
